import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Hammer,
  PlusCircle,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/AdminSharedComponents";
import ExcavationCard from "./ExcavationCard";
import ExcavationDetail from "./ExcavationDetail";
import ExcavationCreationModal from "../components/ExcavationCreationModal";

const ExcavationList = ({
  excavations,
  selectedExcavation,
  onSelect,
  onBack,
  selectedProjectFilter = null, // { name, projectId, count }
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const itemsPerPage = 5;

  // Reset to page 1 when excavations data changes (from filtering)
  useEffect(() => {
    setCurrentPage(1);
  }, [excavations.length]);

  const totalPages = Math.ceil(excavations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentExcavations = excavations.slice(startIndex, endIndex);

  // If an excavation is selected, show details
  if (selectedExcavation) {
    return <ExcavationDetail excavation={selectedExcavation} onBack={onBack} />;
  }

  return (
    <>
      {/* List Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-primary-dark">
          Excavation Points ({excavations.length})
        </h2>
        <div className="flex items-center gap-2">
          {/* Add New Point Button */}
          <Button
            variant="primary"
            className="flex items-center gap-2 text-sm"
            onClick={() => setShowCreationModal(true)}
          >
            <PlusCircle className="h-4 w-4" />
            <span>Add New</span>
          </Button>
        </div>
      </div>

      {/* List Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {currentExcavations.length > 0 ? (
          currentExcavations.map((excavation) => (
            <ExcavationCard
              key={excavation.id}
              excavation={excavation}
              onSelect={onSelect}
              isSelected={selectedExcavation?.id === excavation.id}
            />
          ))
        ) : (
          <div className="text-center py-8">
            {selectedProjectFilter && selectedProjectFilter.projectId ? (
              // Empty state for specific project
              <>
                <AlertCircle className="h-12 w-12 mx-auto text-orange-400 mb-4" />
                <h3 className="text-lg font-semibold text-primary-dark mb-2">
                  Belum Ada Titik Galian
                </h3>
                <p className="text-gray-600 text-sm mb-1">
                  Project:{" "}
                  <span className="font-semibold">
                    {selectedProjectFilter.name}
                  </span>
                </p>
                <p className="text-gray-500 text-xs mb-4">
                  Belum ada excavation points di project ini
                </p>
                <Link
                  to={`/admin/projects/excavation/add?projectId=${selectedProjectFilter.projectId}`}
                >
                  <Button
                    variant="primary"
                    className="flex items-center gap-2 mx-auto"
                  >
                    <PlusCircle className="h-4 w-4" />
                    <span>Tambah Titik Galian</span>
                  </Button>
                </Link>
              </>
            ) : (
              // Empty state for search/filter
              <>
                <Hammer className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-primary-dark mb-2">
                  No Excavation Points Found
                </h3>
                <p className="text-gray-600 text-sm">
                  Try adjusting your search or filter criteria.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Creation Modal */}
      <ExcavationCreationModal
        isOpen={showCreationModal}
        onClose={() => setShowCreationModal(false)}
      />
    </>
  );
};

export default ExcavationList;
